import { useState } from "react";
import { StoryboardPanel } from "../pipeline/StoryboardPanel.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Film, Library, ListVideo, Loader2, ExternalLink, Play, Ban, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import {
  api, type VideoQueueRow, type VideoRunsView, type VideoLibraryView, type VideoReelsView,
  type VideoRunRowView,
} from "../../lib/api.js";

/** The same hash-navigation the Articles page uses to open one article. */
const openArticle = (keywordId: string) => {
  window.location.hash = `/pipeline?open=${encodeURIComponent(keywordId)}`;
};

/**
 * The video pipeline, on one screen.
 *
 * WHY. Every part of making a reel was reachable only from inside one article,
 * so the questions an operator actually asks — which articles are waiting, what
 * is running, what did it cost, is there a clip worth reusing — had no answer
 * anywhere (Dor, 2026-09-24: 89 posts were waiting on footage and the only way
 * to count them was a SQL query; the shot library had eight clips and no screen).
 *
 * FOUR TABS, in the order the work moves: what is waiting, what is running,
 * what came out, and what can be reused. Every row leads back to the article,
 * because that is where the two gates live.
 *
 * IT RUNS THE PIPELINE, IT DOES NOT BYPASS ITS GATES. The end-to-end button
 * walks an article from topics to a scheduled reel and stops dead at both gates,
 * which stay exactly where they were: a person reads the shot table before money
 * is spent, and a person watches the reel before it is published. What the
 * button removes is the four presses that were never decisions.
 */

const TABS = [
  { id: "queue", label: "תור", icon: ListVideo },
  { id: "runs", label: "ריצות", icon: Loader2 },
  { id: "reels", label: "רילים", icon: Film },
  { id: "library", label: "ספריית שוטים", icon: Library },
] as const;
type Tab = (typeof TABS)[number]["id"];

/** What the article needs next, as the operator would say it. */
const NEXT_HE: Record<VideoQueueRow["next"], { label: string; cls: string; hint: string }> = {
  "extract-topics": { label: "לחלץ נושאים", cls: "bg-neutral-200 text-neutral-800", hint: "מאמר שנכתב לפני שחילוץ הנושאים היה קיים. בלי נושא, התוכנית נוטה להיות סיכום של המאמר." },
  plan: { label: "לתכנן ריל", cls: "bg-sky-100 text-sky-900", hint: "קריאת מודל אחת, בערך $0.15. לא קונה חומר." },
  approve: { label: "לאשר תוכנית", cls: "bg-amber-200 text-amber-950", hint: "שער 1: התוכנית כתובה ומחכה שתקרא אותה. אישור פותח את הקנייה." },
  film: { label: "לצלם", cls: "bg-brand-100 text-brand-800", hint: "התוכנית מאושרת. זה השלב שקונה חומר." },
  watch: { label: "לצפות ולשחרר", cls: "bg-emerald-100 text-emerald-900", hint: "שער 2: הריל מוכן ומחכה שתראה אותו לפני פרסום." },
  schedule: { label: "לתזמן ביומן", cls: "bg-violet-100 text-violet-900", hint: "הריל שוחרר. הפוסטים מוכנים לקבל תאריך." },
};

/** What a run is doing, as the operator would say it. */
const RUN_STATUS_HE: Record<VideoRunRowView["status"], { label: string; cls: string }> = {
  running: { label: "רץ", cls: "bg-brand-100 text-brand-800" },
  waiting_gate1: { label: "ממתין לאישור התוכנית", cls: "bg-amber-200 text-amber-950" },
  waiting_gate2: { label: "ממתין לצפייה", cls: "bg-emerald-100 text-emerald-900" },
  done: { label: "הסתיים", cls: "bg-neutral-200 text-neutral-800" },
  error: { label: "נעצר", cls: "bg-rose-100 text-rose-900" },
  cancelled: { label: "בוטל", cls: "bg-neutral-200 text-neutral-600" },
};

const RUN_STEP_HE: Record<VideoRunRowView["step"], string> = {
  topics: "חילוץ נושאים",
  plan: "תכנון",
  gate1: "שער 1",
  film: "צילום",
  gate2: "שער 2",
  schedule: "תזמון",
};

const fmtIl = (iso: string | null) =>
  iso ? new Intl.DateTimeFormat("he-IL", { timeZone: "Asia/Jerusalem", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso)) : "—";

export function VideoPage() {
  const [tab, setTab] = useState<Tab>("queue");
  return (
    <div className="space-y-4" dir="rtl">
      <header>
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <Clapperboard size={18} /> וידאו
        </h1>
        <p className="text-xs text-muted">
          מה מחכה לחומר, מה רץ עכשיו, מה יצא, ומה אפשר למחזר. הרצה מקצה לקצה עוברת את כל השלבים
          ונעצרת בשני השערים: אישור התוכנית לפני שנקנה חומר, וצפייה בריל לפני פרסום.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
              tab === t.id ? "border-brand-700 font-medium text-brand-800" : "border-transparent text-muted"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <WaitingSection />

      {tab === "queue" && <QueueTab />}
      {tab === "runs" && <RunsTab />}
      {tab === "reels" && <ReelsTab />}
      {tab === "library" && <LibraryTab />}
    </div>
  );
}

/**
 * EVERY DECISION THIS PAGE IS WAITING ON, ABOVE THE TABS, ACTIONABLE IN PLACE.
 *
 * The page used to list a plan parked at a gate and offer one button:
 * "פתיחת המאמר". That button leaves for the Pipeline page, opens the article,
 * and leaves the operator to find the Social column and the same plan again
 * (Dor, 2026-10-21: "I'm on the Video page but for everything I have to go
 * back to the pipeline page and open the article and get to the social column
 * and find it — this is not convenient"). Three screens to press a button that
 * this screen already knew about.
 *
 * So the gate comes here instead. Expanding a row mounts the real
 * `StoryboardPanel` — the same shot table, the same contact sheet, the same two
 * gate buttons, not a reimplementation that would drift from it.
 *
 * ABOVE THE TABS DELIBERATELY. A decision somebody is waiting on is not one of
 * four equal views; it is the reason to open this page at all, and putting it
 * behind the right tab is how storyboard 7 sat approved and invisible.
 */
function WaitingSection() {
  const q = useQuery({ queryKey: ["video-runs"], queryFn: () => api.videoRuns(), refetchInterval: 15_000 });
  const rows = q.data?.waiting ?? [];
  const [open, setOpen] = useState<number | null>(null);

  if (rows.length === 0) return null;

  const ASK: Record<string, { label: string; cls: string }> = {
    draft: { label: "קראו ואשרו — שער 1", cls: "border-amber-300 bg-amber-50" },
    filmed: { label: "צפו ושחררו — שער 2", cls: "border-sky-300 bg-sky-50" },
    approved: { label: "מאושר — אפשר לצלם", cls: "border-brand-200 bg-brand-50" },
  };

  return (
    <section className="space-y-1.5">
      <h2 className="text-sm font-semibold">ממתין לך ({rows.length})</h2>
      <ul className="space-y-1.5">
        {rows.map((r) => {
          const ask = ASK[r.status] ?? { label: r.status, cls: "border-line" };
          const isOpen = open === r.storyboardId;
          return (
            <li key={r.storyboardId} className={`rounded-lg border ${ask.cls}`}>
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                <span className="rounded-full bg-surface/70 px-2 py-0.5 text-[11px] font-medium">{ask.label}</span>
                <span className="font-medium">{r.label}</span>
                {r.topicTitle && <span className="text-xs text-muted">{r.topicTitle}</span>}
                {r.warningCount > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-amber-900">
                    <AlertTriangle size={11} /> {r.warningCount}
                  </span>
                )}
                {r.hasContactSheet && <span className="text-[11px] text-muted">יש תמונת מגע</span>}
                <span className="ms-auto flex items-center gap-2">
                  <span className="text-[11px] text-muted">{fmtIl(r.updatedAt)}</span>
                  <button
                    onClick={() => setOpen(isOpen ? null : r.storyboardId)}
                    className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2 py-1 text-[11px]"
                  >
                    {isOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {isOpen ? "סגירה" : "פתיחה כאן"}
                  </button>
                  <button
                    onClick={() => openArticle(r.keywordId)}
                    title="פתיחת המאמר המלא בעמוד הפייפליין"
                    className="rounded-lg border border-line bg-surface px-2 py-1 text-[11px] text-muted"
                  >
                    <ExternalLink size={11} />
                  </button>
                </span>
              </div>
              {/* The real panel, so the gate here and the gate in the article
                  can never be two different screens. */}
              {isOpen && (
                <div className="border-t border-line/60 bg-surface px-3 py-2">
                  <WaitingBoard keywordId={r.keywordId} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * The storyboard panel for one article, with filming wired to the same endpoint
 * the article's own button uses. `onFilm` is not a no-op here: an approved plan
 * whose run has stopped is exactly the row an operator opens this section to
 * push, and offering the button without wiring it would be the old problem in a
 * new place.
 */
function WaitingBoard({ keywordId }: { keywordId: string }) {
  const qc = useQueryClient();
  const film = useMutation({
    // `style: "explainer"` is not a default worth inheriting — filming a plan is
    // always the explainer, and a plan approved as a shot list then rendered as
    // an animated deck is not the thing that was approved.
    //
    // No targetSeconds: the approved plan already carries its own shot
    // durations, and a length typed on this screen would silently override the
    // budget the plan was read and approved under.
    mutationFn: (storyboardId: number) =>
      api.startSocialVideos(keywordId, { style: "explainer", storyboardId }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["storyboards", keywordId] });
      void qc.invalidateQueries({ queryKey: ["social-video-status", keywordId] });
      void qc.invalidateQueries({ queryKey: ["video-runs"] });
    },
  });
  return (
    <>
      <StoryboardPanel keywordId={keywordId} onFilm={(id) => film.mutate(id)} filming={film.isPending} />
      {film.isError && (
        <p className="pt-1 text-[11px] text-rose-900">{(film.error as Error).message}</p>
      )}
    </>
  );
}

function QueueTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["video-queue"], queryFn: () => api.videoQueue(), refetchInterval: 15_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["video-queue"] });
    void qc.invalidateQueries({ queryKey: ["video-runs"] });
  };
  const startOne = useMutation({
    mutationFn: (keywordId: string) => api.videoRunStart({ keywordId }),
    onSettled: refresh,
  });
  if (q.isLoading) return <p className="text-sm text-muted">טוען…</p>;
  const rows = q.data?.queue ?? [];
  const totals = q.data?.totals;

  return (
    <div className="space-y-3">
      {totals && (
        <p className="text-xs text-muted">
          {totals.articles} מאמרים · {totals.posts} פוסטים ממתינים לחומר · כ-${totals.estimatedCostUsd} לכולם
          {totals.articles > 0 ? " (ריל אחד למאמר)" : ""}
          {/* The runnable half, said separately. A single total reads as a plan,
              and it is not one while some rows have no path to a reel at all. */}
          {totals.runnableArticles < totals.articles && (
            <span className="block">
              מתוכם {totals.runnableArticles} אפשר להריץ עכשיו (כ-${totals.runnableCostUsd}).
              היתר הם עמודי שיטה ומאמרי ארכיון מסאניטי, שאין להם מסלול צילום.
            </span>
          )}
        </p>
      )}
      {totals && totals.runnableArticles > 0 && <BatchBar max={totals.runnableArticles} onDone={refresh} />}
      {startOne.data && startOne.data.ok === false && (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">{startOne.data.error}</p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-muted">אין פוסט שממתין לחומר. כל פוסטי הווידאו שנוצרו קיבלו קליפ.</p>
      ) : (
        <table className="w-full text-right text-sm">
          <thead className="border-b border-line text-[11px] text-muted">
            <tr>
              <th className="px-2 py-1.5 font-medium">מאמר</th>
              <th className="px-2 py-1.5 font-medium">ממתינים</th>
              <th className="px-2 py-1.5 font-medium">תוכנית</th>
              <th className="px-2 py-1.5 font-medium">השלב הבא</th>
              <th className="px-2 py-1.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const next = NEXT_HE[r.next];
              return (
                <tr key={`${r.keywordId ?? r.sanityId}`} className="border-b border-line/60 align-top">
                  <td className="px-2 py-2">
                    <span className="font-medium">{r.label}</span>
                    {!r.hasTopics && r.keywordId && (
                      <span className="block text-[10px] text-muted">בלי נושאי וידאו</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {r.waiting}
                    <span className="block text-[10px] text-muted">{r.formats.join(" · ")}</span>
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {r.storyboard ? (
                      <>
                        #{r.storyboard.id} · {r.storyboard.status}
                        {r.storyboard.topicTitle && <span className="block text-[10px] text-muted">{r.storyboard.topicTitle}</span>}
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <span title={next.hint} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${next.cls}`}>
                      {next.label}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {r.run ? (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${RUN_STATUS_HE[r.run.status].cls}`}>
                          {RUN_STATUS_HE[r.run.status].label} · {RUN_STEP_HE[r.run.step]}
                        </span>
                      ) : r.runnable ? (
                        <button
                          onClick={() => startOne.mutate(r.keywordId!)}
                          disabled={startOne.isPending}
                          title="מריץ את כל השרשרת ועוצר בשני השערים. כ-$4.5 לריל."
                          className="flex items-center gap-1 rounded-lg border border-brand-700 px-2 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                        >
                          <Play size={11} /> הרצה מקצה לקצה
                        </button>
                      ) : (
                        /* Said, not hidden: a row with no button and no reason
                           reads as a bug. `blocked` is the reason. */
                        <span title={r.blocked ?? ""} className="text-[11px] text-muted">לא ניתן לצילום</span>
                      )}
                      {r.keywordId && (
                        <button
                          onClick={() => openArticle(r.keywordId!)}
                          className="rounded-lg border border-line px-2 py-1 text-[11px]"
                        >
                          פתיחת המאמר
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * "Run the next N", with the ceiling that makes it safe to press.
 *
 * THE CAP IS NOT OPTIONAL. One press here can buy five reels; the difference
 * between that being a feature and being an accident is a number the operator
 * typed. It is split evenly into a per-run cap, which is what makes it
 * enforceable — each run refuses a step that would take it past its own share.
 */
function BatchBar({ max, onDone }: { max: number; onDone: () => void }) {
  const [count, setCount] = useState(Math.min(3, max));
  const [cap, setCap] = useState(Math.min(3, max) * 5);
  const run = useMutation({
    mutationFn: () => api.videoRunBatch({ count, budgetCapUsd: cap }),
    onSettled: onDone,
  });
  const setBoth = (n: number) => { setCount(n); setCap(n * 5); };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 text-xs">
      <span className="font-medium">הרצה מרובה</span>
      <label className="flex items-center gap-1">
        <input
          type="number" min={1} max={Math.min(20, max)} value={count}
          onChange={(e) => setBoth(Math.max(1, Math.min(Math.min(20, max), Number(e.target.value) || 1)))}
          className="w-14 rounded border border-line px-1.5 py-1"
        />
        מאמרים הבאים בתור
      </label>
      <label className="flex items-center gap-1">
        תקרה כוללת $
        <input
          type="number" min={1} step={1} value={cap}
          onChange={(e) => setCap(Math.max(1, Number(e.target.value) || 1))}
          className="w-16 rounded border border-line px-1.5 py-1"
        />
      </label>
      <span className="text-muted">≈ ${(cap / count).toFixed(2)} לריל</span>
      <button
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className="flex items-center gap-1 rounded-lg border border-brand-700 px-2.5 py-1 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
      >
        {run.isPending ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} התחלה
      </button>
      {/* Every stop is reported. A batch that started three of five and said
          nothing about the other two is the thing that erodes trust in it. */}
      {run.data?.ok && (
        <span className="text-emerald-800">
          יצאו {run.data.started} ריצות, ${run.data.perRunCapUsd} לכל אחת.
          {run.data.refused.length > 0 && ` ${run.data.refused.length} סורבו: ${run.data.refused.join("; ")}`}
        </span>
      )}
      {run.data && run.data.ok === false && (
        <span className="text-rose-900">{(run.data as unknown as { error?: string }).error}</span>
      )}
      {run.isError && <span className="text-rose-900">{(run.error as Error).message}</span>}
    </div>
  );
}

function RunsTab() {
  // Polled fast while something is running: these are the minutes an operator
  // actually watches, and a stale stage line is the thing that makes a person
  // press a paid button twice.
  const q = useQuery({ queryKey: ["video-runs"], queryFn: () => api.videoRuns(), refetchInterval: 5_000 });
  const d: VideoRunsView | undefined = q.data;
  const running = [...(d?.planning ?? []), ...(d?.filming ?? [])].filter((j) => j.status === "running" || j.status === "rendering");
  const interrupted = (d?.filming ?? []).filter((j) => j.status === "interrupted");

  return (
    <div className="space-y-4">
      <section>
        <h2 className="mb-1.5 text-sm font-semibold">רץ עכשיו</h2>
        {running.length === 0 ? (
          <p className="text-sm text-muted">שום דבר לא רץ.</p>
        ) : (
          <ul className="space-y-1.5">
            {running.map((j) => (
              <li key={`${j.keywordId}-${j.startedAt}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <Loader2 size={13} className="animate-spin text-brand-700" />
                <span className="font-medium">{j.label}</span>
                <span className="text-xs text-muted">
                  {"stage" in j && j.stage ? `${j.stage}${j.stageDetail ? ` · ${j.stageDetail}` : ""}` : "מתכנן"}
                </span>
                <span className="ms-auto text-[11px] text-muted">מאז {fmtIl(j.startedAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* A RENDER THAT WAS KILLED SAYS SO. It used to vanish: the job lived in
          memory, a deploy replaced the container, and the next screen showed
          nothing at all — no row, no error, just an article that never moved.
          With 6-13 deploys a day this is the common ending, not a rare one. */}
      {interrupted.length > 0 && (
        <section>
          <h2 className="mb-1.5 text-sm font-semibold">צילומים שנקטעו</h2>
          <ul className="space-y-1.5">
            {interrupted.map((j) => (
              <li key={`${j.keywordId}-${j.startedAt}`} className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                <AlertTriangle size={13} className="text-amber-800" />
                <span className="font-medium">{j.label}</span>
                <span className="text-xs text-amber-900">{j.error ?? "נקטע"}</span>
                <span className="ms-auto text-[11px] text-muted">התחיל {fmtIl(j.startedAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-1.5 text-sm font-semibold">ריצות מקצה לקצה</h2>
        {(d?.runs ?? []).length === 0 ? (
          <p className="text-sm text-muted">לא הופעלה ריצה מקצה לקצה.</p>
        ) : (
          <ul className="space-y-1.5">
            {d!.runs.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${RUN_STATUS_HE[r.status].cls}`}>
                  {RUN_STATUS_HE[r.status].label}
                </span>
                <span className="font-medium">{r.label}</span>
                <span className="text-xs text-muted">{RUN_STEP_HE[r.step]}</span>
                <span className="text-xs text-muted">
                  ${r.spentUsd.toFixed(2)}
                  {r.budgetCapUsd !== null && ` מתוך $${r.budgetCapUsd.toFixed(2)}`}
                </span>
                {r.batchId && <span className="text-[10px] text-muted">הרצה מרובה</span>}
                {/* The reason it stopped, in full. A run that says only
                    "נעצר" sends the operator to the logs. */}
                {r.error && (
                  <span className="flex w-full items-start gap-1 text-[11px] text-rose-900">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {r.error}
                  </span>
                )}
                <span className="ms-auto flex items-center gap-2">
                  <span className="text-[11px] text-muted">{fmtIl(r.updatedAt)}</span>
                  <button onClick={() => openArticle(r.keywordId)} className="rounded-lg border border-line px-2 py-1 text-[11px]">
                    פתיחת המאמר
                  </button>
                  {["running", "waiting_gate1", "waiting_gate2"].includes(r.status) && <CancelRun id={r.id} />}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-1.5 text-sm font-semibold">רילים שצולמו</h2>
        {(d?.recent ?? []).length === 0 ? (
          <p className="text-sm text-muted">עוד לא צולם ריל.</p>
        ) : (
          <table className="w-full text-right text-sm">
            <thead className="border-b border-line text-[11px] text-muted">
              <tr>
                <th className="px-2 py-1.5 font-medium">מאמר</th>
                <th className="px-2 py-1.5 font-medium">נושא</th>
                <th className="px-2 py-1.5 font-medium">מצב</th>
                <th className="px-2 py-1.5 font-medium">אורך</th>
                <th className="px-2 py-1.5 font-medium">עלות</th>
                <th className="px-2 py-1.5 font-medium">צולם</th>
              </tr>
            </thead>
            <tbody>
              {d!.recent.map((r) => (
                <tr key={r.storyboardId} className="border-b border-line/60">
                  <td className="px-2 py-2">{r.label}</td>
                  <td className="px-2 py-2 text-xs text-muted">{r.topicTitle ?? "המאמר כולו"}</td>
                  <td className="px-2 py-2 text-xs">{r.status === "released" ? "שוחרר" : "ממתין לצפייה"}</td>
                  <td className="px-2 py-2 text-xs">{r.renderSeconds ? `${Math.round(r.renderSeconds)}s` : "—"}</td>
                  <td className="px-2 py-2 text-xs">${r.spentUsd.toFixed(2)}</td>
                  <td className="px-2 py-2 text-xs text-muted">{fmtIl(r.filmedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** Stops a run advancing. Nothing already paid for is thrown away. */
function CancelRun({ id }: { id: number }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.videoRunCancel(id),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["video-runs"] });
      void qc.invalidateQueries({ queryKey: ["video-queue"] });
    },
  });
  return (
    <button
      onClick={() => m.mutate()}
      disabled={m.isPending}
      title="הריצה תפסיק להתקדם. צעד ששולם עליו כבר ונמצא באוויר יסתיים ויירשם."
      className="flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] text-muted hover:bg-canvas disabled:opacity-50"
    >
      <Ban size={11} /> ביטול
    </button>
  );
}

function ReelsTab() {
  const q = useQuery({ queryKey: ["video-reels"], queryFn: () => api.videoReels() });
  const reels: VideoReelsView["reels"] = q.data?.reels ?? [];
  if (q.isLoading) return <p className="text-sm text-muted">טוען…</p>;
  if (reels.length === 0) return <p className="text-sm text-muted">עוד לא נוצר ריל.</p>;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {reels.map((r) => (
        <div key={r.storyboardId} className="space-y-2 rounded-xl border border-line p-3">
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium">{r.label}</span>
            <span className={`ms-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] ${r.status === "released" ? "bg-emerald-100 text-emerald-900" : "bg-sky-100 text-sky-900"}`}>
              {r.status === "released" ? "שוחרר" : "ממתין לצפייה"}
            </span>
          </div>
          <p className="text-[11px] text-muted">{r.topicTitle ?? "המאמר כולו"} · {r.renderSeconds ? `${Math.round(r.renderSeconds)} שניות` : ""}</p>
          {r.assetUrl ? (
            <video src={r.assetUrl} controls playsInline preload="metadata" className="w-full rounded-lg border border-line bg-black" />
          ) : (
            <p className="text-xs text-danger">אין קובץ מקושר</p>
          )}
          <ul className="space-y-0.5 text-[11px]">
            {r.posts.map((p, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-muted">{p.platform}/{p.format}</span>
                <span>{p.status}</span>
                {p.postizReleaseUrl && (
                  <a href={p.postizReleaseUrl} target="_blank" rel="noreferrer" className="text-brand-700">
                    <ExternalLink size={11} />
                  </a>
                )}
              </li>
            ))}
          </ul>
          <button
            onClick={() => openArticle(r.keywordId)}
            className="w-full rounded-lg border border-line px-2 py-1 text-[11px]"
          >
            התוכנית והשערים
          </button>
        </div>
      ))}
    </div>
  );
}

function LibraryTab() {
  const q = useQuery({ queryKey: ["video-library"], queryFn: () => api.videoLibrary() });
  const d: VideoLibraryView | undefined = q.data;
  if (q.isLoading) return <p className="text-sm text-muted">טוען…</p>;
  const clips = d?.clips ?? [];

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        כל קליפ שנקנה נשמר כאן עם התגיות שלו. {d?.reuse.probes ?? 0} בדיקות שימוש חוזר רצו עד היום,
        ומתוכן {d?.reuse.wouldHaveMatched ?? 0} מצאו קליפ מתאים. כרגע זו מדידה בלבד: הצינור עדיין מייצר כל שוט מחדש.
      </p>
      {clips.length === 0 ? (
        <p className="text-sm text-muted">הספרייה ריקה.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          {clips.map((c) => (
            <div key={c.id} className="space-y-1.5 rounded-xl border border-line p-2">
              {c.sanity_url ? (
                <video src={c.sanity_url} controls playsInline preload="metadata" muted className="w-full rounded-lg bg-black" />
              ) : (
                <div className="flex h-24 items-center justify-center rounded-lg bg-canvas text-[11px] text-muted">אין תצוגה</div>
              )}
              <p className="text-[11px] font-medium">{c.subject}</p>
              <p className="text-[10px] text-muted">
                {[c.role, c.setting, c.time_of_day, c.mood].filter(Boolean).join(" · ")}
              </p>
              <p className="text-[10px] text-muted">
                {c.duration_sec ? `${c.duration_sec}s · ` : ""}${(c.cost_usd ?? 0).toFixed(2)} · שימושים: {c.times_used ?? 0}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
